import {
    createIdempotencyKey,
    getCreemSubscription,
    mutateCreemSubscription,
    openCreemCustomerPortal,
    reconcileCreemSubscription,
} from './creem-billing-api';
import {
    appendTextElement,
    clearElement,
    errorMessage,
    formatDate,
    formatInteger,
    formatMoney,
    formatStorageMb,
    setBillingStatus,
    subscriptionStatusLabel,
    tBilling,
} from './creem-billing-dom';
import '../css/creem-subscription-discount.scss';

const mutationKeys = new Map();
const monthlyBillingPeriod = 'every-month';
const annualBillingPeriod = 'every-year';

const itemBillingPeriod = (item, fallback = monthlyBillingPeriod) => (
    item?.billing_period
    || (String(item?.product_code || '').endsWith('_ANNUAL')
        ? annualBillingPeriod
        : fallback)
);

const renderResource = (container, label, value, formatter = formatInteger) => {
    const item = document.createElement('div');
    item.className = 'creem-subscription__resource';
    appendTextElement(item, 'span', '', label);
    appendTextElement(item, 'strong', '', formatter(value));
    container.appendChild(item);
};

const renderPrice = (container, {
    amount,
    effective_amount: effectiveAmount,
    monthly_equivalent_amount: monthlyEquivalentAmount,
    effective_monthly_equivalent_amount: effectiveMonthlyEquivalentAmount,
    billing_period: billingPeriod,
    currency,
    discount,
    perMonth = false,
}) => {
    const price = document.createElement('div');
    price.className = 'creem-subscription__price';
    const annual = billingPeriod === annualBillingPeriod;
    const fullEffectiveAmount = effectiveAmount ?? amount;
    const displayedAmount = annual
        ? (effectiveMonthlyEquivalentAmount ?? (Number(fullEffectiveAmount) / 12))
        : fullEffectiveAmount;
    const originalDisplayedAmount = annual
        ? (monthlyEquivalentAmount ?? (Number(amount) / 12))
        : amount;
    const discounted = discount && Number(fullEffectiveAmount) !== Number(amount);
    if (discounted) {
        appendTextElement(
            price,
            'span',
            'creem-subscription__price-original',
            formatMoney(originalDisplayedAmount, currency),
        );
    }
    appendTextElement(
        price,
        'strong',
        'creem-subscription__price-effective',
        `${formatMoney(displayedAmount, currency)}${(perMonth || annual) ? ` ${tBilling('catalog.perMonth')}` : ''}`,
    );
    if (discounted) {
        appendTextElement(
            price,
            'span',
            'creem-subscription__discount-badge',
            `-${discount.percent}%`,
        );
    }
    container.appendChild(price);
    if (annual) {
        const annualMeta = document.createElement('div');
        annualMeta.className = 'creem-subscription__annual-meta';
        const savings = appendTextElement(
            annualMeta,
            'span',
            'creem-product__annual-savings',
            tBilling('catalog.twoMonthsFree'),
        );
        savings.dataset.creemAnnualSavings = 'true';
        const charge = appendTextElement(
            annualMeta,
            'span',
            'creem-product__annual-charge',
            tBilling('catalog.billedAnnually', {
                amount: formatMoney(fullEffectiveAmount, currency),
            }),
        );
        charge.dataset.creemAnnualCharge = 'true';
        container.appendChild(annualMeta);
    }
};

const actionButton = (action, productCode = '') => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = action === 'cancel'
        ? 'btn-outline-red creem-subscription__action'
        : 'outline-button creem-subscription__action';
    button.dataset.creemSubscriptionAction = action;
    if (productCode) {
        button.dataset.creemProductCode = productCode;
    }
    button.textContent = tBilling(`subscription.${action}`);
    return button;
};

const blockedPlanButton = () => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'outline-button creem-subscription__action';
    button.disabled = true;
    button.dataset.creemBlockedPlan = 'change_one_dimension';
    button.textContent = tBilling('catalog.changeOneDimension');
    return button;
};

const renderConfirmation = (container, {
    action,
    productCode,
    messageKey = action,
    messageOptions = {},
    onConfirm,
}) => {
    clearElement(container);
    container.hidden = false;
    appendTextElement(
        container,
        'p',
        'creem-subscription__confirmation-text',
        tBilling(`subscription.confirm.${messageKey}`, messageOptions),
    );
    const actions = document.createElement('div');
    actions.className = 'creem-subscription__confirmation-actions';
    const confirm = document.createElement('button');
    confirm.type = 'button';
    confirm.className = action === 'cancel' ? 'btn-outline-red' : 'filled-button';
    confirm.dataset.creemConfirmAction = action;
    confirm.textContent = tBilling('subscription.confirmAction');
    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'outline-button';
    back.textContent = tBilling('subscription.keepCurrent');
    back.addEventListener('click', () => {
        clearElement(container);
        container.hidden = true;
    });
    confirm.addEventListener('click', () => onConfirm(confirm, action, productCode));
    actions.append(confirm, back);
    container.appendChild(actions);
    confirm.focus();
};

export const initCreemSubscriptionModal = (i18nReady = Promise.resolve()) => {
    const modal = document.getElementById('manageSubscriptionModal');
    if (
        !modal
        || modal.dataset.creemEnabled !== 'true'
        || modal.dataset.creemInitialized === 'true'
    ) {
        return;
    }
    modal.dataset.creemInitialized = 'true';
    const spinner = modal.querySelector('.spinner-container');
    const footer = modal.querySelector('.modal-footer');
    const content = modal.querySelector('[data-creem-subscription-content]');

    const load = async () => {
        await i18nReady;
        spinner.style.display = '';
        content.hidden = true;
        footer.style.display = 'none';
        clearElement(content);
        try {
            const state = await getCreemSubscription();
            render(state);
        } catch (error) {
            content.hidden = false;
            setBillingStatus(content, {
                variant: 'danger',
                message: errorMessage(error, 'subscription.loadError'),
                persistent: true,
            });
        } finally {
            spinner.style.display = 'none';
            footer.style.display = '';
        }
    };

    const runMutation = async (button, action, productCode, status, confirmation) => {
        if (button.disabled) {
            return;
        }
        button.disabled = true;
        button.textContent = tBilling('subscription.saving');
        const intent = `${action}:${productCode || ''}`;
        const key = mutationKeys.get(intent) || createIdempotencyKey();
        mutationKeys.set(intent, key);
        try {
            await mutateCreemSubscription(action, productCode, key);
            mutationKeys.delete(intent);
            clearElement(confirmation);
            confirmation.hidden = true;
            setBillingStatus(status, {
                variant: 'success',
                message: tBilling('subscription.changeAccepted'),
                persistent: true,
            });
            await load();
        } catch (error) {
            const unknown = (
                error?.code === 'subscription_change_outcome_unknown'
                || error?.code === 'subscription_change_in_progress'
                || !error?.code
            );
            if (!unknown) {
                mutationKeys.delete(intent);
            }
            button.disabled = false;
            button.textContent = tBilling('subscription.confirmAction');
            setBillingStatus(status, {
                variant: unknown ? 'warning' : 'danger',
                message: errorMessage(error),
                persistent: true,
            });
        }
    };

    const render = (state) => {
        clearElement(content);
        content.className = 'creem-subscription';
        content.hidden = false;
        const status = document.createElement('div');
        status.className = 'creem-billing-status';
        status.hidden = true;
        content.appendChild(status);

        if (!state.subscription) {
            appendTextElement(
                content,
                'p',
                'creem-subscription__empty',
                tBilling('subscription.none'),
            );
            return;
        }

        const subscription = state.subscription;
        const currentBillingPeriod = itemBillingPeriod(subscription);
        const header = document.createElement('div');
        header.className = 'creem-subscription__header';
        const titleGroup = document.createElement('div');
        appendTextElement(titleGroup, 'h3', '', subscription.product_name);
        appendTextElement(
            titleGroup,
            'span',
            `creem-subscription__status creem-subscription__status--${subscription.status}`,
            subscriptionStatusLabel(subscription.status),
        );
        renderPrice(header, {
            ...subscription,
            billing_period: currentBillingPeriod,
            perMonth: true,
        });
        header.prepend(titleGroup);
        content.appendChild(header);

        const period = subscription.current_period_end
            ? tBilling('subscription.periodEnd', {
                date: formatDate(subscription.current_period_end),
            })
            : '';
        if (period) {
            appendTextElement(content, 'p', 'creem-subscription__period', period);
        }
        if (subscription.discount?.ends_at) {
            appendTextElement(
                content,
                'p',
                'creem-subscription__discount-until',
                tBilling('subscription.discountUntil', {
                    date: formatDate(subscription.discount.ends_at),
                }),
            );
        }

        if (subscription.status === 'past_due' && subscription.grace_period_end) {
            const graceStatus = document.createElement('div');
            graceStatus.dataset.creemPastDueWarning = 'true';
            setBillingStatus(graceStatus, {
                variant: 'warning',
                message: tBilling('subscription.pastDueGrace', {
                    date: formatDate(subscription.grace_period_end),
                }),
                persistent: true,
            });
            content.appendChild(graceStatus);
        }

        const resources = document.createElement('div');
        resources.className = 'creem-subscription__resources';
        renderResource(resources, tBilling('catalog.tokens'), subscription.token_quantity);
        renderResource(resources, tBilling('catalog.connections'), subscription.connection_quantity);
        renderResource(resources, tBilling('catalog.accounts'), subscription.account_quantity);
        renderResource(
            resources,
            tBilling('catalog.delayedActions'),
            subscription.delayed_operations_limit,
        );
        renderResource(
            resources,
            tBilling('catalog.imageFiles'),
            subscription.image_asset_file_limit,
        );
        renderResource(
            resources,
            tBilling('catalog.imageStorage'),
            subscription.image_asset_storage_mb,
            formatStorageMb,
        );
        content.appendChild(resources);

        const latestChangePending = state.latest_change && [
            'pending',
            'accepted',
            'outcome_unknown',
        ].includes(state.latest_change.status);
        const planChangePending = (
            state.plan_change_blocked_reason === 'subscription_change_pending'
        );
        if (latestChangePending || planChangePending) {
            setBillingStatus(status, {
                variant: state.latest_change?.status === 'outcome_unknown' ? 'warning' : 'info',
                message: tBilling('subscription.pendingChange'),
                persistent: true,
            });
        }

        const confirmation = document.createElement('div');
        confirmation.className = 'creem-subscription__confirmation';
        confirmation.hidden = true;

        const confirm = (button, action, productCode) => runMutation(
            button,
            action,
            productCode,
            status,
            confirmation,
        );
        const openConfirmation = (button, option = null) => {
            let messageKey = button.dataset.creemSubscriptionAction;
            if (option?.transition_dimension === 'billing_period') {
                messageKey = option.action === 'upgrade'
                    ? 'billingPeriodUpgrade'
                    : 'billingPeriodDowngrade';
            } else if (
                button.dataset.creemSubscriptionAction === 'cancel'
                && subscription.status === 'past_due'
            ) {
                messageKey = 'cancelPastDue';
            }
            renderConfirmation(confirmation, {
                action: button.dataset.creemSubscriptionAction,
                productCode: button.dataset.creemProductCode || '',
                messageKey,
                messageOptions: {
                    date: formatDate(subscription.current_period_end),
                },
                onConfirm: confirm,
            });
        };

        const planOptions = [
            ...(state.plan_options || []),
            ...(state.blocked_plan_options || []),
        ];
        if (planOptions.length) {
            appendTextElement(content, 'h4', 'creem-subscription__section-title', tBilling('subscription.otherPlans'));
            const availablePeriods = new Set([
                currentBillingPeriod,
                ...planOptions.map((option) => (
                    itemBillingPeriod(option, currentBillingPeriod)
                )),
            ]);
            let selectedBillingPeriod = currentBillingPeriod;
            const periodToggle = document.createElement('div');
            periodToggle.className = 'billing-period-toggle creem-subscription__billing-period-toggle';
            periodToggle.dataset.creemBillingPeriodToggle = 'true';
            periodToggle.setAttribute('role', 'group');
            periodToggle.setAttribute('aria-label', tBilling('catalog.billingPeriod'));
            [
                [annualBillingPeriod, tBilling('catalog.annual')],
                [monthlyBillingPeriod, tBilling('catalog.monthly')],
            ].forEach(([period, label]) => {
                if (!availablePeriods.has(period)) {
                    return;
                }
                const toggleButton = appendTextElement(
                    periodToggle,
                    'button',
                    'billing-period-toggle__button',
                    label,
                );
                toggleButton.type = 'button';
                toggleButton.dataset.billingPeriod = period;
                toggleButton.setAttribute(
                    'aria-pressed',
                    period === selectedBillingPeriod ? 'true' : 'false',
                );
            });
            content.appendChild(periodToggle);
            const plans = document.createElement('div');
            plans.className = 'creem-subscription__plans';
            const renderPlanOptions = () => {
                clearElement(plans);
                planOptions
                    .filter((option) => (
                        itemBillingPeriod(option, currentBillingPeriod)
                        === selectedBillingPeriod
                    ))
                    .forEach((option) => {
                        const row = document.createElement('div');
                        row.className = 'creem-subscription__plan';
                        const copy = document.createElement('div');
                        appendTextElement(copy, 'strong', '', option.name);
                        renderPrice(copy, {
                            ...option,
                            billing_period: itemBillingPeriod(
                                option,
                                currentBillingPeriod,
                            ),
                        });
                        const button = option.blocked_reason
                            ? blockedPlanButton()
                            : actionButton(option.action, option.product_code);
                        if (!option.blocked_reason) {
                            button.addEventListener('click', () => (
                                openConfirmation(button, option)
                            ));
                        }
                        row.append(copy, button);
                        plans.appendChild(row);
                    });
            };
            periodToggle.querySelectorAll('[data-billing-period]').forEach((button) => {
                button.addEventListener('click', () => {
                    selectedBillingPeriod = button.dataset.billingPeriod;
                    periodToggle.querySelectorAll('[data-billing-period]')
                        .forEach((candidate) => candidate.setAttribute(
                            'aria-pressed',
                            candidate === button ? 'true' : 'false',
                        ));
                    clearElement(confirmation);
                    confirmation.hidden = true;
                    renderPlanOptions();
                });
            });
            renderPlanOptions();
            content.appendChild(plans);
        }

        const actions = document.createElement('div');
        actions.className = 'creem-subscription__actions';
        (state.available_actions || [])
            .filter((action) => ['pause', 'resume', 'cancel'].includes(action))
            .forEach((action) => {
                const button = actionButton(action);
                button.addEventListener('click', () => openConfirmation(button));
                actions.appendChild(button);
            });

        if (state.customer_portal_available) {
            const portal = document.createElement('button');
            portal.type = 'button';
            portal.className = 'outline-button creem-subscription__action';
            portal.dataset.creemCustomerPortal = 'true';
            portal.textContent = tBilling('subscription.portal');
            portal.addEventListener('click', async () => {
                if (portal.disabled) {
                    return;
                }
                portal.disabled = true;
                try {
                    const response = await openCreemCustomerPortal();
                    window.location.assign(response.url);
                } catch (error) {
                    portal.disabled = false;
                    setBillingStatus(status, {
                        variant: 'danger',
                        message: errorMessage(error),
                        persistent: true,
                    });
                }
            });
            actions.appendChild(portal);
        }
        content.append(actions, confirmation);

        if (state.latest_change?.can_reconcile === true) {
            const reconcile = document.createElement('button');
            reconcile.type = 'button';
            reconcile.className = 'outline-button creem-subscription__reconcile';
            reconcile.textContent = tBilling('subscription.reconcile');
            reconcile.addEventListener('click', async () => {
                reconcile.disabled = true;
                try {
                    await reconcileCreemSubscription();
                    await load();
                } catch (error) {
                    reconcile.disabled = false;
                    setBillingStatus(status, {
                        variant: 'warning',
                        message: errorMessage(error),
                        persistent: true,
                    });
                }
            });
            content.appendChild(reconcile);
        }
    };

    modal.addEventListener('show.bs.modal', load);
    modal.addEventListener('shown.bs.modal', () => {
        modal.querySelector('.modal-title')?.focus();
    });
};
